import { useLocation } from "react-router";
import { useEffect, useState, lazy, Suspense, useCallback } from "react";
import { Route, Routes } from "react-router-dom";
import Navbar from "./Component/Navbar/Navbar";
import DotConnectionBackground from "./Component/Home/DotConnectionBackground";
import Footer from "./Component/Footer/Footer";
import { FaArrowUp } from "react-icons/fa";
import "./App.css";

// Lazy load all components
const Home = lazy(() => import("./Component/Home/Home"));
const About = lazy(() => import("./Component/About/About"));
const Careers = lazy(() => import("./Component/Careers/Careers"));
const ContactUs = lazy(() => import("./Component/ContactUs/ContactUs"));
const ContractManufacturing = lazy(() => import("./Component/ContractManufacturing/ContractManufacturing"));
const ServiceDetail = lazy(() => import("./Component/Home/Expertise/ServiceData"));
const RapidToolingSection = lazy(() => import("./Component/RapidToolingSection/RapidToolingSection"));
const DLP3DPrinting = lazy(() => import("./Component/Services/3DPrinting/DLP3DPrinting"));
const FDM3DPrinting = lazy(() => import("./Component/Services/3DPrinting/FDM3DPrinting"));
const MJF3DPrinting = lazy(() => import("./Component/Services/3DPrinting/MJF3DPrinting"));
const SLA3DPrinting = lazy(() => import("./Component/Services/3DPrinting/SLA3DPrinting"));
const SLS3DPrinting = lazy(() => import("./Component/Services/3DPrinting/SLS3DPrinting"));
const InjectionMolding = lazy(() => import("./Component/Services/InjectionMolding/InjectionMolding"));
const MaterialsSection = lazy(() => import("./Component/Services/MaterialsSection/MaterialsSection"));
const VacuumCasting = lazy(() => import("./Component/Services/VacuumCasting/VacuumCasting"));
const SignUp = lazy(() => import("./Component/SignUp/SignUp"));
const Login = lazy(() => import("./Component/SignUp/LogIn"));
const UploadFile = lazy(() => import("./Component/UploadFile"));
const QuotationForm = lazy(() => import("./Component/QuotationForm"));
const Invoice = lazy(() => import("./Component/Invoice"));
const Generate = lazy(() => import("./Component/Generate"));
const NegotiateButton = lazy(() => import("./Component/NegotiateButton"));
const PrivacyPolicy = lazy(() => import("./Component/Terms/PrivacyPolicy"));
const Disclaimer = lazy(() => import("./Component/Terms/Disclaimer"));
const TermsConditions = lazy(() => import("./Component/Terms/TermsConditions"));
const ForgotPassword = lazy(() => import("./Component/SignUp/ForgotPassword"));
const ResetPassword = lazy(() => import("./Component/SignUp/ResetPassword"));
const PromoSection = lazy(() => import("./Component/PromoSection/PromoSection"));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  const { pathname } = useLocation();
  const [showTopBtn, setShowTopBtn] = useState(false);

  // Memoized scroll handlers
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handleScroll = useCallback(() => {
    setShowTopBtn(window.scrollY > 300);
  }, []);

  // Optimized useEffect
  useEffect(() => {
    scrollToTop();
  }, [pathname, scrollToTop]);

  useEffect(() => {
    // Throttled scroll event listener
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });
    return () => window.removeEventListener("scroll", throttledScroll);
  }, [handleScroll]);

  return (
    <>
      <Navbar />
      <DotConnectionBackground />
      
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/fdm-3d-printing" element={<FDM3DPrinting />} />
          <Route path="/sla-3d-printing" element={<SLA3DPrinting />} />
          <Route path="/sls-3d-printing" element={<SLS3DPrinting />} />
          <Route path="/dlp-3d-printing" element={<DLP3DPrinting />} />
          <Route path="/mjf-3d-printing" element={<MJF3DPrinting />} />
          <Route path="/vacuum-casting" element={<VacuumCasting />} />
          <Route path="/injection-molding" element={<InjectionMolding />} />
          <Route path="/available-materials" element={<MaterialsSection />} />
          <Route path="/extra-discount" element={<PromoSection />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/contractManufacturing" element={<ContractManufacturing />} />
          <Route path="/rapidToolingSection" element={<RapidToolingSection />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/log-in" element={<Login />} />
          <Route path="/quotation" element={<UploadFile />} />
          <Route path="/quotation-form" element={<QuotationForm />} />
          <Route path="/invoice" element={<Invoice />} />
          <Route path="/generate" element={<Generate />} />
          <Route path="/negotiate" element={<NegotiateButton />} />
        </Routes>
      </Suspense>

      <Footer />
      
      {showTopBtn && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-blue-500 opacity-80 text-white flex items-center justify-center shadow-lg hover:bg-blue-800 transition-all duration-300"
          aria-label="Go to top"
        >
          <FaArrowUp className="text-xl" />
        </button>
      )}
    </>
  );
}

export default App;